<template>
  <div class="page-content">
    <div class="content-wrapper">
      <div class="content d-flex justify-content-center align-items-center">
        <b-card>
          <b-card-header>
            <language-switcher/>
          </b-card-header>
          <b-card-body>
            <div class="text-center mb-3">
              <i
                class="icon-reading icon-2x text-slate-300 border-slate-300 border-3 rounded-round p-3 mb-3 mt-1"
              />
              <h5 class="mb-0">{{$t('companies.company_application_form')}}</h5>
            </div>

            <b-form v-if="showForm" ref="form" class="application-form" @submit.prevent="save">
              <b-row>
                <b-col v-show="currentStep === steps.COMPANY">
                  <fields ungrouped/>

                  <fields group="ride_types" class="ride_types"/>
                  <div
                      v-if="isFormLoaded && (model.ride_types != null && model.ride_types.includes('transfer'))"
                      class="form-group row"
                      style="text-align: right !important;display: block;max-width: 74%;">
                    <span v-html="$t('general.choose_airports_where_you_work')"></span>
                  </div>
                  <div v-if="isFormLoaded">
                    <fields v-if="showAirportsList" :first="true" group="airports"/>

                    <radius-field
                      v-if="isFormLoaded && (model.ride_types != null && model.ride_types.includes('city_taxi'))"
                      :address-field="fields.get('address')"
                      :radius-field="fields.get('city_taxi_radius')"
                      :model="model"
                    />

                    <radius-field
                      v-if="isFormLoaded && (model.ride_types != null && model.ride_types.includes('delivery'))"
                      :address-field="fields.get('delivery_area_center')"
                      :radius-field="fields.get('delivery_radius')"
                      :model="model"
                    />
                  </div>
                </b-col>
                <b-col v-show="currentStep === steps.VEHICLES">
                  <vehicles-list-edit
                    v-if="vehicles.length > 0"
                    v-model="model.vehicles"
                    :ride-types="enabledRideTypes"
                    :vehicles="vehicles"
                  />
                </b-col>

                <b-col v-show="currentStep === steps.BILLING">
                  <fields group="billing"/>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-button
                    v-if="canBack"
                    type="button"
                    squared
                    @click.prevent="clickBack"
                  >
                    {{ $t('general.back') }}
                  </b-button>
                </b-col>
                <b-col class="text-right">
                  <b-button
                    :disabled="!canNext"
                    type="button"
                    variant="primary"
                    squared
                    @click="clickNext"
                  >
                    {{ buttonNextLabel }}
                  </b-button>
                </b-col>
              </b-row>
            </b-form>

            <b-alert :show="saved" variant="success">
              {{ saveMessage }}
            </b-alert>
          </b-card-body>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import {ResourceFormMixin} from 'stylemix-vue-admin'
import ApplicationApi from './ApplicationApi'
import TimeZones from '../components/timezones.json'
import RadiusField from '../components/fields/RadiusField'
import VehiclesListEdit from '../companies/VehiclesListEdit'
import FormMixin from './FormMixin'
import LanguageSwitcher from '../language-switcher/LanguageSwitcher'

const steps = {
  COMPANY: 'company',
  VEHICLES: 'vehicles',
  BILLING: 'billing',
}

export default {
  name: 'ApplicationForm',
  components: {LanguageSwitcher, RadiusField, VehiclesListEdit},
  mixins: [ResourceFormMixin, FormMixin],

  data() {
    return {
      currentStep: steps.COMPANY,
      isFormLoaded: false,
      radiusShape: null,
      rideTypes: [],
      saved: false,
      saveMessage: '',
      stepsOrder: [steps.COMPANY, steps.VEHICLES, steps.BILLING],
      vehicles: [],
      emailVerified: false
    }
  },

  computed: {
    buttonNextLabel() {
      return this.nextStep ? this.$t('general.next') : this.$t('general.save')
    },
    canBack() {
      return this.stepsOrder.indexOf(this.currentStep) > 0
    },
    canNext() {
      if (this.currentStep !== steps.VEHICLES) {
        return true
      }
      for (const vehicles of Object.values(this.model.vehicles)) {
        if (vehicles.length > 0) {
          return true
        }
      }

      return false
    },
    enabledRideTypes() {
      if (!this.model.ride_types) {
        return []
      }

      return this.rideTypes.filter(t => {
        return this.model.ride_types.includes(t.value)
      })
    },
    nextStep() {
      return this.stepsOrder[this.stepsOrder.indexOf(this.currentStep) + 1]
    },
    showForm() {
      return !this.saved && this.isFormLoaded
    },
    steps() {
      return steps
    },
    showAirportsList() {
      return this.model.ride_types && this.model.ride_types.includes('transfer')
    },
  },

  watch: {
    currentStep(val, oldVal) {
      if (val === steps.VEHICLES) {
        this.loadVehicles().then(() => {
          if (this.vehicles.length === 0) {
            this.stepsOrder.indexOf(oldVal) > this.stepsOrder.indexOf(val)
              ? this.clickBack()
              : this.clickNext()
          }
        })
      }
    },
  },

  mounted() {
    this.loadForm().then(result => {
      let timezones = []
      for (let timezone of TimeZones) {
        timezones.push({
          label: timezone.text,
          value: timezone.utc[0],
        })
      }
      this.fields.get('timezone').options = timezones

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          this.model.address.latlng = `${position.coords.latitude}, ${position.coords.longitude}`
          this.model.address.zoom = 12
        })
      }
      this.rideTypes = result.ride_types

      if (!this.model.vehicles || this.model.vehicles.length === 0) {
        this.model.vehicles = Object.fromEntries(
          this.rideTypes.map(type => {
            return [type.value, []]
          }),
        )
      }

      this.isFormLoaded = true

      this.setFieldGroup('ride_types', ['ride_types'])
      this.setFieldGroup('hidden', ['vehicles'])
    })
  },

  methods: {
    api() {
      return new ApplicationApi()
    },
    async clickNext() {
      if(await this.validateFirstStep()) {
        if (this.nextStep) {
          this.currentStep = this.nextStep
          return
        }
        this.save().catch()
      }
    },
    isFieldEmpty(val) {
      return val === undefined || val === null || val === '' || val.length === 0;
    },
    clickBack() {
      this.currentStep = this.stepsOrder[
      this.stepsOrder.indexOf(this.currentStep) - 1
        ]
    },
    loadVehicles() {
      const promise = this.api()
        .getVehicles(this.model.address, this.model.ride_types, this.model.delivery_area_center, this.model.airports)
        .then(result => {
          this.vehicles = result.data
        })
      return this.$uiBlocker(promise, this.$el)
    },
    onSaved(result) {
      this.saved = true
      this.saveMessage = result.message
    },
    async validateEmail() {
      await this.api()
        .checkEmail(this.model.email)
        .then(result => {
          this.emailVerified = true;
        }).catch(er => {
          this.emailVerified = false;
        })
    },
    async validateFirstStep() {
      document.querySelectorAll('.application-form .error').forEach((el) => {
        el.classList.remove('error')
      });
      let validated = true;
      for (let prop in this.model) {
        if (prop === 'is_owner_operator')
          continue;
        let input = document.querySelectorAll('.application-form #' + prop);
        if (this.isFieldEmpty(this.model.ride_types)) {
          document.querySelectorAll('.application-form .ride_types input[type="checkbox"]').forEach((el) => {
            el.classList.add('error')
          });
        }
        if (this.model.ride_types.includes('transfer')) {
          if (this.isFieldEmpty(this.model.airports)) {
            document.querySelector('.application-form label[for="airports"]').classList.add('error')
            validated = false;
          }
          this.model.airports.forEach((airport, index) => {
            if (this.isFieldEmpty(airport.direction)) {
              document.querySelector(`.application-form [attribute="airports.${index}.direction"]`).classList.add('error');
              validated = false;
            }
            if (this.isFieldEmpty(airport.id)) {
              document.querySelector(`.application-form [attribute="airports.${index}.id"]`).classList.add('error')
              validated = false;
            }
          });
        }
        if (this.model.ride_types.includes('delivery') && this.isFieldEmpty(this.model.delivery_area_center.address)) {
          document.querySelectorAll('.application-form #delivery_area_center-address')[0].classList.add('error')
          validated = false;
        }
        if (this.model.ride_types.includes('city_taxi') && (this.isFieldEmpty(this.model.address.address))) {
          document.querySelectorAll('.application-form #address-address')[0].classList.add('error')
          validated = false;
        }
        if (this.isFieldEmpty(this.model.timezone)) {
          document.querySelector(`[attribute="timezone"] select`).classList.add('error')
          validated = false;
        }
        if (input.length && this.isFieldEmpty(this.model[prop])) {
          document.querySelectorAll('.application-form #' + prop)[0].classList.add('error')
          validated = false;
        }
      }
      if (validated) {
        await this.validateEmail()
        if (!this.emailVerified) {
          validated = false
          document.querySelectorAll('.application-form #email')[0].classList.add('error')
        }
      }

      return validated;
    },
  }
}
</script>

<style>
.ride_types .form-check-inline .form-check-input {
  margin-top: 0;
}

.application-form .form-group input.error, .application-form .form-group select.error {
  border: 1px solid red;
}

label.error {
  color: red;
}

.application-form .form-group input[type="checkbox"].error + label {
  color: red;
}

.error .v-select,
.error select {
  border-color: red;
}

.application-form .vs__dropdown-menu {
  width: 355px !important;
}

.application-form .form-control.v-select .vs__dropdown-toggle input[type='search'] {
  min-height: calc(1.75rem + 2px);
}

.application-form .form-control.v-select {
  min-height: calc(2.05rem + 2px);
}
</style>
